/**
 * SF-4758: Items (admin:priorities:manage). Add, edit, mark done and remove open items; reopen
 * done and removed ones (they are kept as history); change how many items each person ranks.
 *
 * SF-5080: the items of the list the page shows, a filter by where open items came from, the
 * waiting suggestions to decide on, and Eva's proposed groupings of them.
 *
 * SF-5087: the filter starts on Added by product, which counts accepted suggestions too (product
 * confirmed them; they keep a From a suggestion tag). A Show descriptions switch, kept in this
 * browser, shows each item's description under its title, and a tag says how many companies
 * want each item.
 */
import { useEffect, useState } from 'react';
import {
  App,
  Button,
  Card,
  InputNumber,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatShortDate } from '../../../utils/date';
import {
  createItemArgs,
  prioritiesErrorMessage,
  setItemStatusArgs,
  setTopNArgs,
  updateItemArgs,
  usePriorityItems,
  usePrioritiesWrite,
  type ItemInput,
  type ItemStatus,
  type PriorityItem,
  type PriorityList,
} from '../api/prioritiesClient';
import { TOP_N_MAX, TOP_N_MIN } from '../constants';
import { ItemFormModal } from './ItemFormModal';
import { LoadError, RefreshWarning } from './LoadError';
import { SuggestionsPanel } from './SuggestionsPanel';
import { EvaGroupingsPanel } from './EvaGroupingsPanel';
import { JiraLinks } from './JiraLinks';
import { WantedByCompanies } from './WantedByCompanies';

/** SF-5087: where the Show descriptions choice is kept, per browser. */
export const SHOW_DESCRIPTIONS_KEY = 'priorities.showDescriptions';

const readShowDescriptions = () => {
  try {
    return window.localStorage.getItem(SHOW_DESCRIPTIONS_KEY) === 'true';
  } catch {
    return false;
  }
};

const titleCell = (showDescriptions: boolean) => {
  const TitleCell = (title: string, item: PriorityItem) => {
    return (
      <>
        <Space size={4} wrap>
          {title}
          <JiraLinks links={item.jiraLinks} />
          {item.source === 'suggestion' && <Tag color="purple">From a suggestion</Tag>}
          <WantedByCompanies companies={item.companies} />
          {!!item.suggestedCount && (
            <Typography.Text type="secondary">
              {item.suggestedCount} {item.suggestedCount === 1 ? 'suggestion' : 'suggestions'}{' '}
              folded in
            </Typography.Text>
          )}
        </Space>
        {showDescriptions && item.description && (
          <Typography.Paragraph
            type="secondary"
            style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}
            ellipsis={{ rows: 6, expandable: true, symbol: 'Show more' }}
          >
            {item.description}
          </Typography.Paragraph>
        )}
      </>
    );
  };
  return TitleCell;
};

/**
 * Added by product: every item in the table, accepted suggestions included (waiting ones have
 * their own card). From a suggestion: only those that began as one.
 */
type SourceFilter = 'product' | 'suggestion';
const SOURCE_OPTIONS: Array<{ value: SourceFilter; label: string }> = [
  { value: 'product', label: 'Added by product' },
  { value: 'suggestion', label: 'From a suggestion' },
];

interface Props {
  topN: number;
  listId: string;
  lists?: PriorityList[];
}

export const ItemsTab: React.FC<Props> = ({ topN, listId, lists = [] }) => {
  const { message } = App.useApp();
  // One read for both tables, so a status change can never show an item in both or in neither
  // (sf-api lists every open item first, and the open-item cap is well under the list cap).
  const itemsQuery = usePriorityItems('all', true, listId);
  const suggestionsQuery = usePriorityItems('suggested', true, listId);
  const suggestions = suggestionsQuery.data?.data?.items ?? [];
  // Deciding on a suggestion only while the waiting ones shown are known to be current: their
  // last read worked and no read is retrying one that failed (they may have been decided since).
  const canDecide =
    !suggestionsQuery.isError &&
    !(
      suggestionsQuery.isFetching &&
      suggestionsQuery.errorUpdatedAt > suggestionsQuery.dataUpdatedAt
    );
  // Filtered here: the one read above already has every item with its source (missing = admin).
  const [source, setSource] = useState<SourceFilter>('product');
  const [showDescriptions, setShowDescriptions] = useState(readShowDescriptions);
  const toggleDescriptions = (on: boolean) => {
    setShowDescriptions(on);
    try {
      window.localStorage.setItem(SHOW_DESCRIPTIONS_KEY, String(on));
    } catch {
      // Private windows can refuse storage; the switch still works until the page reloads.
    }
  };
  const write = usePrioritiesWrite();
  const [editing, setEditing] = useState<PriorityItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [n, setN] = useState<number | null>(topN);

  useEffect(() => setN(topN), [topN]);

  const items = itemsQuery.data?.data?.items ?? [];
  const open = items.filter((i) => i.status === 'open');
  const shownOpen = source === 'suggestion' ? open.filter((i) => i.source === 'suggestion') : open;
  const closed = items.filter((i) => i.status !== 'open');

  const setStatus = async (item: PriorityItem, status: ItemStatus, done: string) => {
    try {
      await write.run(setItemStatusArgs(item.id, status));
      void message.success(done);
    } catch (error) {
      void message.error(prioritiesErrorMessage(error, 'The item could not be changed.'));
    }
  };

  const submitItem = async (values: ItemInput) => {
    try {
      await write.run(editing ? updateItemArgs(editing.id, values) : createItemArgs(values));
    } catch (error) {
      throw new Error(prioritiesErrorMessage(error, 'The item could not be saved.'));
    }
  };

  const saveTopN = async () => {
    if (n === null) return;
    try {
      await write.run(setTopNArgs(n));
      void message.success(`Each person's top ${n} ranked items now count`);
    } catch (error) {
      void message.error(prioritiesErrorMessage(error, 'The number could not be saved.'));
    }
  };

  const openColumns: ColumnsType<PriorityItem> = [
    { title: 'Item', dataIndex: 'title', render: titleCell(showDescriptions) },
    { title: 'Added', dataIndex: 'createdAt', width: 180, render: formatShortDate },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      render: (_, item) => (
        <Space>
          <Button
            size="small"
            aria-label={`Edit ${item.title}`}
            onClick={() => {
              setEditing(item);
              setFormOpen(true);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            aria-label={`Mark ${item.title} done`}
            onClick={() => setStatus(item, 'done', 'Marked done')}
          >
            Mark done
          </Button>
          <Button
            size="small"
            danger
            aria-label={`Remove ${item.title}`}
            onClick={() => setStatus(item, 'removed', 'Removed')}
          >
            Remove
          </Button>
        </Space>
      ),
    },
  ];

  const closedColumns: ColumnsType<PriorityItem> = [
    { title: 'Item', dataIndex: 'title', render: titleCell(showDescriptions) },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 200,
      render: (status: ItemStatus, item) => {
        if (status !== 'done') return <Tag>Removed</Tag>;
        return (
          <Space size={4}>
            <Tag color="success">Done</Tag>
            {item.completedBy === 'jira' && (
              <Typography.Text type="secondary">Closed in Jira</Typography.Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'When',
      key: 'when',
      width: 180,
      render: (_, item) => formatShortDate(item.completedAt ?? item.removedAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, item) => (
        <Button
          size="small"
          aria-label={`Reopen ${item.title}`}
          onClick={() => setStatus(item, 'open', 'Reopened')}
        >
          Reopen
        </Button>
      ),
    },
  ];

  // The full error only when there is nothing to show. A failed refetch (every write refreshes
  // every priorities read) keeps the cached list, with a warning above it.
  if (itemsQuery.isError && !itemsQuery.data) {
    return (
      <LoadError
        error={itemsQuery.error}
        fallback="The items could not be loaded."
        onRetry={itemsQuery.refetch}
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {itemsQuery.isError && (
        <RefreshWarning
          error={itemsQuery.error}
          fallback="The items could not be refreshed."
          onRetry={itemsQuery.refetch}
        />
      )}
      <Card size="small" title="How many ranked items count">
        <Space>
          <InputNumber
            aria-label="How many ranked items count"
            min={TOP_N_MIN}
            max={TOP_N_MAX}
            precision={0}
            value={n}
            onChange={setN}
          />
          <Button onClick={saveTopN} disabled={n === null || n === topN} loading={write.isLoading}>
            Save
          </Button>
          <Typography.Text type="secondary">
            Between {TOP_N_MIN} and {TOP_N_MAX}. Lowering it keeps everyone's ranking; only the top
            ones count.
          </Typography.Text>
        </Space>
      </Card>
      <Card
        size="small"
        title="Open items"
        extra={
          <Space wrap>
            <Space size={4}>
              <Switch
                aria-label="Show descriptions"
                size="small"
                checked={showDescriptions}
                onChange={toggleDescriptions}
              />
              <Typography.Text>Show descriptions</Typography.Text>
            </Space>
            <Segmented<SourceFilter>
              aria-label="Where items came from"
              options={SOURCE_OPTIONS}
              value={source}
              onChange={setSource}
            />
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              Add item
            </Button>
          </Space>
        }
      >
        <Table<PriorityItem>
          rowKey="id"
          loading={itemsQuery.isLoading}
          columns={openColumns}
          dataSource={shownOpen}
          pagination={false}
          locale={{
            emptyText: source === 'suggestion' ? 'No open items of this kind' : 'No open items yet',
          }}
        />
      </Card>
      {suggestionsQuery.isError && !suggestionsQuery.data ? (
        <LoadError
          error={suggestionsQuery.error}
          fallback="The waiting suggestions could not be loaded."
          onRetry={suggestionsQuery.refetch}
        />
      ) : (
        <>
          {/* A failed refresh keeps the last answer, with deciding turned off until it works:
              those suggestions may already have been decided. */}
          {suggestionsQuery.isError && (
            <RefreshWarning
              error={suggestionsQuery.error}
              fallback="The waiting suggestions could not be refreshed."
              onRetry={suggestionsQuery.refetch}
            />
          )}
          <SuggestionsPanel
            listId={listId}
            lists={lists}
            suggestions={suggestions}
            loading={suggestionsQuery.isLoading}
            canDecide={canDecide}
          />
          <EvaGroupingsPanel
            listId={listId}
            lists={lists}
            suggestions={suggestions}
            openItems={open}
            canDecide={canDecide}
            onRefresh={suggestionsQuery.refetch}
          />
        </>
      )}
      <Card size="small" title="Done and removed">
        <Table<PriorityItem>
          rowKey="id"
          loading={itemsQuery.isLoading}
          columns={closedColumns}
          dataSource={closed}
          pagination={false}
          locale={{ emptyText: 'Nothing done or removed yet' }}
        />
      </Card>
      <ItemFormModal
        open={formOpen}
        item={editing}
        listId={listId}
        lists={lists}
        onSubmit={submitItem}
        onClose={() => setFormOpen(false)}
      />
    </Space>
  );
};
